import { useEffect, useRef, useState } from "react";

import type { ApiClient, SchemaVersion } from "../../shared/api/client";
import { useLocale } from "../../shared/i18n";
import type { MessageKey } from "../../shared/i18n";
import { Badge, Button, Sheet } from "../../shared/ui";

type HistoryApi = Pick<ApiClient, "listVersions" | "draftFromVersion">;

const REFUSALS: Record<string, MessageKey> = {
  draft_exists: "history.draftExists",
  version_not_restorable: "history.notRestorable",
  draft_has_no_changes: "history.noChanges",
};

function codeOf(cause: unknown): string | undefined {
  const detail =
    cause instanceof Error
      ? (cause.cause as { code?: unknown } | undefined)
      : undefined;
  return typeof detail?.code === "string" ? detail.code : undefined;
}

/**
 * Version history of a section (ADR 0037). Picking a prior version opens a
 * Draft that restores it on top of the active one; publishing that Draft is
 * the next version, so history only ever grows forward.
 */
export function VersionHistorySheet({
  open,
  onOpenChange,
  api,
  abilityId,
  sectionName,
  onDraftReady,
  onOpenExistingDraft,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  api: HistoryApi;
  abilityId: string;
  sectionName: string;
  /** The restoring Draft exists: go and review it. */
  onDraftReady: () => void;
  /** A Draft was already open: go and look at it instead. */
  onOpenExistingDraft: () => void;
}) {
  const { locale, t } = useLocale();
  const [versions, setVersions] = useState<SchemaVersion[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<MessageKey | null>(null);

  // The caller may build `api` on every render; the list is fetched per opening.
  const apiRef = useRef(api);
  useEffect(() => {
    apiRef.current = api;
  });
  useEffect(() => {
    if (!open) return;
    let active = true;
    apiRef.current
      .listVersions(abilityId)
      .then((list) => {
        if (active) setVersions(list);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });
    return () => {
      active = false;
      // Closing resets the sheet, so the next opening starts from a clean slate.
      setVersions(null);
      setLoadFailed(false);
      setRefusal(null);
    };
  }, [abilityId, open]);

  const restore = (version: SchemaVersion) => {
    setBusy(true);
    setRefusal(null);
    api
      .draftFromVersion(abilityId, version.id)
      .then(() => {
        onDraftReady();
      })
      .catch((cause: unknown) => {
        setRefusal(REFUSALS[codeOf(cause) ?? ""] ?? "history.failed");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={`${t("history.title")} «${sectionName}»`}
      description={t("history.hint")}
    >
      {refusal && (
        <div role="alert" className="mb-3 text-body text-warning">
          <p>{t(refusal)}</p>
          {refusal === "history.draftExists" && (
            <Button
              className="mt-2"
              size="sm"
              variant="secondary"
              onClick={onOpenExistingDraft}
            >
              {t("sections.edit.draft")}
            </Button>
          )}
        </div>
      )}
      {loadFailed ? (
        <p className="text-body text-error">{t("history.loadFailed")}</p>
      ) : versions === null ? (
        <p className="text-body text-fg-muted" aria-busy="true">
          {t("startup.loadingRoute")}
        </p>
      ) : (
        <ul>
          {versions.map((version) => (
            <li
              key={version.id}
              className="flex min-h-12 items-center gap-3 border-b border-line-subtle py-2 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="text-body font-semibold">
                  {t("history.version")} {version.version_number}
                </p>
                <p className="text-caption text-fg-muted">
                  {new Date(version.published_at).toLocaleDateString(locale, {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
              {version.active ? (
                <Badge tone="success">{t("history.current")}</Badge>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy}
                  aria-label={`${t("history.restoreVersion")} ${String(version.version_number)}`}
                  onClick={() => {
                    restore(version);
                  }}
                >
                  {t("history.restore")}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Sheet>
  );
}
